import { useEffect, useState, useRef, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Maximize } from 'lucide-react';
import { useDisplayState } from '../hooks/useDisplayState';
import SheetVoteDisplay from './SheetVoteDisplay';
import SheetVoteResults from './SheetVoteResults';
import SheetWinnerFullscreen from './SheetWinnerFullscreen';
import StreamingMode from './StreamingMode';
import PollPrepareDisplay from './PollPrepareDisplay';
import PollVotingDisplay from './PollVotingDisplay';
import PollResultsDisplay from './PollResultsDisplay';
import BondVoteDisplay from './BondVoteDisplay';
import BondResultsDisplay from './BondResultsDisplay';
import BondWinnerDisplay from './BondWinnerDisplay';
import BondAllWinnersDisplay from './BondAllWinnersDisplay';
import LotteryDisplay from './LotteryDisplay';
import ChallengeDisplay from './ChallengeDisplay';
import ChallengeResultDisplay from './ChallengeResultDisplay';
import BattleDisplay from './BattleDisplay';
import HeroHPDisplay from './HeroHPDisplay';
import BondAutoAssignDisplay from './BondAutoAssignDisplay';
import { BattleEffectsOverlay } from '../components/BattleEffectsOverlay';

function DisplayMain() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = searchParams.get('session');
  const [isLoadingActiveSession, setIsLoadingActiveSession] = useState(!sessionId);

  const { mode, isConnected, socket } = useDisplayState(sessionId);
  const [needsInteraction, setNeedsInteraction] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [screenShake, setScreenShake] = useState(false);
  const shakeTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const [showFullscreenBtn, setShowFullscreenBtn] = useState(true);
  const hideTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const videoRef = useRef<HTMLVideoElement>(null);

  // Fullscreen toggle
  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch(() => {});
    } else {
      document.exitFullscreen().catch(() => {});
    }
  }, []);

  // Track fullscreen state
  useEffect(() => {
    const handler = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handler);
    return () => document.removeEventListener('fullscreenchange', handler);
  }, []);

  // Auto-hide fullscreen button after 5 seconds, show on mouse move
  useEffect(() => {
    hideTimeoutRef.current = setTimeout(() => setShowFullscreenBtn(false), 5000);

    const handleMouseMove = () => {
      setShowFullscreenBtn(true);
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = setTimeout(() => setShowFullscreenBtn(false), 3000);
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      clearTimeout(hideTimeoutRef.current);
    };
  }, []);

  // Screen shake on certain effects
  const SHAKE_EFFECTS = ['fireball', 'transformation', 'battle-cry'];
  useEffect(() => {
    if (!socket) return;
    const handleEffect = (data: { effectType: string }) => {
      if (SHAKE_EFFECTS.includes(data.effectType)) {
        if (shakeTimeoutRef.current) clearTimeout(shakeTimeoutRef.current);
        setScreenShake(true);
        shakeTimeoutRef.current = setTimeout(() => setScreenShake(false), 2000);
      }
    };
    socket.on('display:battle-effect', handleEffect);
    return () => {
      socket.off('display:battle-effect', handleEffect);
      if (shakeTimeoutRef.current) clearTimeout(shakeTimeoutRef.current);
    };
  }, [socket]);

  // Try to play video with audio; if browser blocks, show click overlay
  const tryPlayVideo = useCallback((video: HTMLVideoElement) => {
    video.play().catch(() => {
      setNeedsInteraction(true);
    });
  }, []);

  const handleUnlockAudio = () => {
    setNeedsInteraction(false);
    if (videoRef.current) {
      videoRef.current.play();
    }
  };

  // Auto-redirect to active session if no session specified
  useEffect(() => {
    if (!sessionId) {
      const fetchActiveSession = async () => {
        try {
          const apiBase = import.meta.env.VITE_API_URL || '';
          const response = await fetch(`${apiBase}/api/sessions/active`);
          if (response.ok) {
            const data = await response.json();
            if (data.session) {
              console.log('[Display] Auto-redirecting to active session:', data.session.id);
              navigate(`/display?session=${data.session.id}`, { replace: true });
            }
          }
        } catch (error) {
          console.error('[Display] Error fetching active session:', error);
        } finally {
          setIsLoadingActiveSession(false);
        }
      };

      fetchActiveSession();
    }
  }, [sessionId, navigate]);

  const renderProjection = () => {
    // Show loading while fetching active session
    if (isLoadingActiveSession) {
      return (
        <div className="h-screen flex items-center justify-center display-dark-bg">
          <div className="text-center">
            <div className="animate-spin rounded-full h-32 w-32 border-b-8 border-parchment-300 mx-auto mb-8"></div>
            <p className="text-3xl text-parchment-200 font-heading">Ricerca sessione attiva...</p>
          </div>
        </div>
      );
    }

    // Show error if no session found
    if (!sessionId) {
      return (
        <div className="h-screen flex items-center justify-center display-dark-bg">
          <div className="text-center">
            <p className="text-3xl text-parchment-100 font-display mb-4">Nessuna Sessione Attiva</p>
            <p className="text-xl text-parchment-400 font-heading">Crea e attiva una sessione dal pannello admin</p>
          </div>
        </div>
      );
    }

    // Show connection status if not connected
    if (!isConnected && sessionId) {
      return (
        <div className="h-screen flex items-center justify-center display-dark-bg">
          <div className="text-center">
            <div className="animate-spin rounded-full h-32 w-32 border-b-8 border-parchment-300 mx-auto mb-8"></div>
            <p className="text-3xl text-parchment-200 font-heading">Connessione al server...</p>
          </div>
        </div>
      );
    }

    switch (mode.type) {
      case 'standby':
        return (
          <div className="h-screen w-screen bg-black">
            <img
              src="/images/dungeonWorld_screen.png"
              alt="Dungeon World"
              className="w-full h-full object-cover"
            />
          </div>
        );

      case 'sheet-vote':
        return sessionId ? (
          <SheetVoteDisplay
            sessionId={mode.sessionId || sessionId}
            activeTeam={mode.activeTeam}
          />
        ) : (
          <div className="h-screen flex items-center justify-center">
            <p className="text-2xl text-red-400 font-heading">Nessun ID sessione fornito</p>
          </div>
        );

      case 'sheet-vote-results':
        // Show vote results with percentages (same component as voting, but shows closed votes)
        return sessionId ? (
          <SheetVoteDisplay
            sessionId={mode.sessionId || sessionId}
            showClosedVotes={true}
            activeTeam={mode.activeTeam}
          />
        ) : (
          <div className="h-screen flex items-center justify-center">
            <p className="text-2xl text-red-400 font-heading">Nessun ID sessione fornito</p>
          </div>
        );

      case 'sheet-vote-winners':
        // Show full character sheet for active team winner
        return sessionId ? (
          <SheetWinnerFullscreen
            sessionId={mode.sessionId || sessionId}
            activeTeam={mode.activeTeam}
          />
        ) : (
          <div className="h-screen flex items-center justify-center">
            <p className="text-2xl text-red-400 font-heading">Nessun ID sessione fornito</p>
          </div>
        );

      case 'sheet-vote-all-winners':
        // Show all winners for all teams
        return sessionId ? (
          <SheetVoteResults
            sessionId={mode.sessionId || sessionId}
            showAllTeams={true}
          />
        ) : (
          <div className="h-screen flex items-center justify-center">
            <p className="text-2xl text-red-400 font-heading">Nessun ID sessione fornito</p>
          </div>
        );

      case 'message':
        return (
          <div className="h-screen flex items-center justify-center display-dark-bg">
            <div className="text-center max-w-4xl px-8">
              <p className="text-6xl font-bold text-parchment-100 font-display">{mode.text}</p>
            </div>
          </div>
        );

      case 'backdrop':
        // Se url è 'black', mostra schermo nero
        if (mode.url === 'black') {
          return <div className="h-screen w-screen bg-black" />;
        }
        return (
          <div className="h-screen w-screen bg-black">
            <img
              src={mode.url}
              alt="Backdrop"
              className="w-full h-full object-cover"
            />
          </div>
        );

      case 'video':
        return (
          <div className="h-screen w-screen bg-black flex items-center justify-center">
            <video
              ref={videoRef}
              key={mode.url}
              src={mode.url}
              playsInline
              onLoadedData={(e) => tryPlayVideo(e.currentTarget)}
              className="w-full h-full object-contain"
            />
            {needsInteraction && (
              <div
                className="absolute inset-0 flex items-center justify-center bg-black/80 cursor-pointer z-10"
                onClick={handleUnlockAudio}
              >
                <p className="text-white text-4xl font-bold">Clicca per avviare il video</p>
              </div>
            )}
          </div>
        );

      case 'streaming':
        return <StreamingMode streamUrl={mode.streamUrl} />;

      case 'poll-prepare':
        return <PollPrepareDisplay targetTeam={mode.targetTeam} />;

      case 'poll-voting':
        return (
          <PollVotingDisplay
            pollId={mode.pollId}
            targetTeam={mode.targetTeam}
            socket={socket}
          />
        );

      case 'poll-results':
        return <PollResultsDisplay pollId={mode.pollId} />;

      case 'bond-vote':
        return (
          <BondVoteDisplay
            voteId={mode.voteId}
            activeTeam={mode.activeTeam}
          />
        );

      case 'bond-vote-results':
        return (
          <BondResultsDisplay
            voteId={mode.voteId}
            activeTeam={mode.activeTeam}
          />
        );

      case 'bond-vote-winner':
        return (
          <BondWinnerDisplay
            voteId={mode.voteId}
            fromTeam={mode.fromTeam}
            heroName={mode.heroName}
            heroImageUrl={mode.heroImageUrl}
            bondText={mode.bondText}
          />
        );

      case 'bond-vote-all-winners':
        return sessionId ? (
          <BondAllWinnersDisplay sessionId={mode.sessionId || sessionId} />
        ) : (
          <div className="h-screen flex items-center justify-center">
            <p className="text-2xl text-red-400 font-heading">Nessun ID sessione fornito</p>
          </div>
        );

      case 'bond-auto-assign':
        return (
          <BondAutoAssignDisplay
            allBonds={mode.allBonds}
            winners={mode.winners}
          />
        );

      case 'lottery':
        return (
          <LotteryDisplay
            lotteryId={mode.lotteryId}
            socket={socket}
          />
        );

      case 'lottery-winner':
        return (
          <LotteryDisplay
            lotteryId={mode.lotteryId}
            socket={socket}
          />
        );

      case 'challenge-active':
        return (
          <ChallengeDisplay
            challengeId={mode.challengeId}
            variant={mode.variant}
            config={mode.config}
            socket={socket}
            targetGrid={mode.targetGrid}
            initialCollectiveGrid={mode.collectiveGrid}
            initialAverageAccuracy={mode.averageAccuracy}
          />
        );

      case 'challenge-result':
        return (
          <ChallengeResultDisplay
            challengeId={mode.challengeId}
            variant={mode.variant}
            result={mode.result}
            finalScore={mode.finalScore}
            targetScore={mode.targetScore}
            whiteClicks={mode.whiteClicks}
            blackClicks={mode.blackClicks}
            averageAccuracy={mode.averageAccuracy}
            collectiveAccuracy={mode.collectiveAccuracy}
            collectiveGrid={mode.collectiveGrid}
            targetGrid={mode.targetGrid}
          />
        );

      case 'battle':
        return (
          <BattleDisplay
            battleState={mode.battleState}
            socket={socket}
          />
        );

      case 'hero-hp':
        return (
          <HeroHPDisplay
            mode={mode as any}
            socket={socket}
          />
        );

      case 'consiglio':
        return (
          <div className="h-screen flex items-center justify-center display-dark-bg">
            <div className="text-center max-w-4xl px-8">
              <p className="text-7xl font-bold text-parchment-100 font-display">Invia il tuo consiglio</p>
            </div>
          </div>
        );

      case 'consiglio-entry':
        return (
          <div className="h-screen w-screen bg-black flex items-center justify-center p-16">
            <div className="max-w-5xl text-center">
              <p className="text-5xl md:text-6xl lg:text-7xl font-bold text-white font-display leading-tight">
                &laquo;{mode.text}&raquo;
              </p>
              <p className="text-2xl text-white/70 italic font-heading mt-6 text-right">
                &mdash; {mode.nickname}
              </p>
            </div>
          </div>
        );

      default:
        return (
          <div className="h-screen flex items-center justify-center display-dark-bg">
            <p className="text-3xl text-parchment-400 font-heading">Modalità proiezione: {mode.type}</p>
          </div>
        );
    }
  };

  return (
    <div className="w-full h-full relative">
      {screenShake && (
        <style>{`
          @keyframes display-shake {
            0%, 100% { transform: translate(0, 0); }
            10% { transform: translate(-6px, 4px); }
            20% { transform: translate(5px, -6px); }
            30% { transform: translate(-8px, 2px); }
            40% { transform: translate(4px, -4px); }
            50% { transform: translate(-4px, 6px); }
            60% { transform: translate(6px, -2px); }
            70% { transform: translate(-3px, 5px); }
            80% { transform: translate(5px, -3px); }
            90% { transform: translate(-2px, 2px); }
          }
        `}</style>
      )}
      <div style={screenShake ? { animation: 'display-shake 0.4s ease-in-out 5' } : undefined}>
        {renderProjection()}
      </div>
      <BattleEffectsOverlay socket={socket} />
      {!isFullscreen && (
        <button
          onClick={toggleFullscreen}
          className={`absolute top-4 right-4 z-50 p-3 bg-black/60 hover:bg-black/80 text-white rounded-lg transition-opacity duration-300 cursor-pointer ${
            showFullscreenBtn ? 'opacity-100' : 'opacity-0 pointer-events-none'
          }`}
          title="Schermo intero"
        >
          <Maximize size={24} />
        </button>
      )}
    </div>
  );
}

export default DisplayMain;
